import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { BarChart3, TrendingUp, BookOpen, PlayCircle, Award, Calendar, Clock } from 'lucide-react';

interface QuizResult {
  lessonId: number;
  lessonName: string;
  score: number;
  submittedAt: string;
}

interface QuizzesProgressData {
  totalQuizzes: number;
  solvedQuizzes: number;
  results: QuizResult[];
}

interface LessonsProgressData {
  totalVideos: number;
  watchedVideos: number;
  progress: number;
}

interface ApiResponse<T> {
  success: boolean;
  data: T;
  message: string | null;
}

const Statistics: React.FC = () => {
  const { currentUser } = useAuth();
  const [quizzesData, setQuizzesData] = useState<QuizzesProgressData | null>(null);
  const [lessonsData, setLessonsData] = useState<LessonsProgressData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (currentUser?.id) {
      fetchStatistics();
    } else if (currentUser === null) {
      // User is not logged in
      setError('يجب تسجيل الدخول لعرض الإحصائيات');
      setLoading(false);
    }
  }, [currentUser]);

  const fetchStatistics = async () => {
    setLoading(true);
    setError(null);

    console.log('Fetching statistics for user:', currentUser?.id);

    if (!currentUser?.id) {
      setError('معرف المستخدم غير متوفر');
      setLoading(false);
      return;
    }

    let lessonsSuccess = false;
    let quizzesSuccess = false;

    // Fetch lessons data
    try {
      const lessonsResponse = await api.get(`/api/Student/GetStudentLessonsProgress/${currentUser.id}`);
      console.log('Lessons Response:', lessonsResponse.data);

      const responseData = lessonsResponse.data as any;
      if (responseData && responseData.success) {
        setLessonsData(responseData.data);
        console.log('Lessons data set:', responseData.data);
        lessonsSuccess = true;
      } else {
        console.log('Lessons response not successful');
        setLessonsData({ totalVideos: 0, watchedVideos: 0, progress: 0 });
      }
    } catch (lessonsError) {
      console.error('Error fetching lessons data:', lessonsError);
      setLessonsData({ totalVideos: 0, watchedVideos: 0, progress: 0 });
    }

    // Fetch quizzes data
    try {
      const quizzesResponse = await api.get(`/api/Student/GetStudentQuizzesProgress/${currentUser.id}`);
      console.log('Quizzes Response:', quizzesResponse.data);

      const responseData = quizzesResponse.data as any;
      if (responseData && responseData.success) {
        setQuizzesData(responseData.data);
        console.log('Quizzes data set:', responseData.data);
        quizzesSuccess = true;
      } else {
        console.log('Quizzes response not successful');
        setQuizzesData({ totalQuizzes: 0, solvedQuizzes: 0, results: [] });
      }
    } catch (quizzesError) {
      console.error('Error fetching quizzes data:', quizzesError);
      setQuizzesData({ totalQuizzes: 0, solvedQuizzes: 0, results: [] });
    }

    // Only set error if both APIs fail
    if (!lessonsSuccess && !quizzesSuccess) {
      setError('فشل في تحميل الإحصائيات - تعذر الاتصال بالخادم');
    }

    setLoading(false);
  };

  const CircularProgress = ({ percentage, size = 120, strokeWidth = 8, color = '#f97316' }: {
    percentage: number;
    size?: number;
    strokeWidth?: number;
    color?: string;
  }) => {
    const radius = (size - strokeWidth) / 2;
    const circumference = 2 * Math.PI * radius;
    const safePercentage = isNaN(percentage) ? 0 : Math.max(0, Math.min(100, percentage));
    const strokeDashoffset = circumference - (safePercentage / 100) * circumference;

    return (
      <div className="relative" style={{ width: size, height: size }}>
        <svg width={size} height={size} className="transform -rotate-90">
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="transparent"
            stroke="#e5e7eb"
            strokeWidth={strokeWidth}
          />
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="transparent"
            stroke={color}
            strokeWidth={strokeWidth}
            strokeDasharray={circumference}
            strokeDashoffset={strokeDashoffset}
            strokeLinecap="round"
            className="transition-all duration-1000 ease-out"
          />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-2xl font-bold text-gray-700">{Math.round(safePercentage)}%</span>
        </div>
      </div>
    );
  };

  const BarChart = ({ data }: { data: QuizResult[] }) => {
    const maxScore = Math.max(...data.map(item => item.score), 100);
    
    return (
      <div className="space-y-4">
        {data.slice(0, 6).map((item, index) => (
          <div key={item.lessonId} className="flex items-center space-x-4 space-x-reverse">
            <div className="flex-1">
              <div className="flex justify-between items-center mb-1">
                <span className="text-sm font-medium text-gray-700 truncate">{item.lessonName}</span>
                <span className="text-sm font-bold text-orange-600">{item.score.toFixed(1)}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3">
                <div
                  className="bg-gradient-to-r from-orange-400 to-orange-500 h-3 rounded-full transition-all duration-1000 ease-out"
                  style={{ width: `${(item.score / maxScore) * 100}%` }}
                />
              </div>
              <span className="text-xs text-gray-500 mt-1">
                {new Date(item.submittedAt).toLocaleDateString('ar-EG')}
              </span>
            </div>
          </div>
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 to-blue-50 pt-20 pb-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-orange-500 mx-auto"></div>
            <p className="mt-4 text-gray-600">جاري تحميل الإحصائيات...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 to-blue-50 pt-20 pb-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg">
              <p>{error}</p>
              <button
                onClick={fetchStatistics}
                className="mt-2 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
              >
                إعادة المحاولة
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const videoProgress = lessonsData && lessonsData.totalVideos > 0 
    ? (lessonsData.watchedVideos / lessonsData.totalVideos) * 100 
    : 0;
  const quizProgress = quizzesData && quizzesData.totalQuizzes > 0 
    ? (quizzesData.solvedQuizzes / quizzesData.totalQuizzes) * 100 
    : 0;
  const averageScore = quizzesData?.results && quizzesData.results.length > 0
    ? quizzesData.results.reduce((acc, result) => acc + result.score, 0) / quizzesData.results.length 
    : 0;

  return (
    <>
      <Helmet>
        {/* Primary Meta Tags */}
        <title>احصائياتي - منصة مستر وليد المصري | تتبع تقدمك في اللغة الإنجليزية</title>
        <meta name="title" content="احصائياتي - منصة مستر وليد المصري | تتبع تقدمك في اللغة الإنجليزية" />
        <meta name="description" content="تتبع تقدمك في تعلم اللغة الإنجليزية مع مستر وليد المصري. احصائيات مفصلة عن دروسك المكتملة، نتائج الاختبارات، ومعدل التقدم في جميع الكورسات." />
        <meta name="keywords" content="احصائيات, تتبع التقدم, دروس مكتملة, نتائج اختبارات, مستر وليد المصري, منصة تعليمية, تعلم الانجليزي, تقدم دراسي" />
        <meta name="author" content="مستر وليد المصري" />
        <meta name="robots" content="noindex, nofollow" />
        
        {/* Canonical URL */}
        <link rel="canonical" href="https://mohamed-said.com/statistics" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://mohamed-said.com/statistics" />
        <meta property="og:title" content="احصائياتي - منصة مستر وليد المصري" />
        <meta property="og:description" content="تتبع تقدمك في تعلم اللغة الإنجليزية مع مستر وليد المصري. احصائيات مفصلة عن دروسك المكتملة ونتائج الاختبارات." />
        <meta property="og:image" content="https://mohamed-said.com/favicon.png" />
        <meta property="og:image:width" content="512" />
        <meta property="og:image:height" content="512" />
        <meta property="og:image:alt" content="احصائياتي - منصة مستر وليد المصري" />
        <meta property="og:site_name" content="منصة مستر وليد المصري التعليمية" />
        <meta property="og:locale" content="ar_AR" />

        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content="https://mohamed-said.com/statistics" />
        <meta property="twitter:title" content="احصائياتي - منصة مستر وليد المصري" />
        <meta property="twitter:description" content="تتبع تقدمك في تعلم اللغة الإنجليزية مع مستر وليد المصري." />
        <meta property="twitter:image" content="https://mohamed-said.com/favicon.png" />
        <meta property="twitter:creator" content="@mohamed_said" />

        {/* Additional SEO Meta Tags */}
        <meta name="language" content="Arabic" />
        <meta name="geo.region" content="EG" />
        <meta name="subject" content="احصائيات تعليمية" />
        <meta name="category" content="تعليم" />
        <meta name="classification" content="منصة تعليمية" />
      </Helmet>
      
      <div className="min-h-screen bg-gradient-to-br from-orange-50 to-blue-50 pt-20 pb-8" dir="rtl">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            إحصائياتي التعليمية
          </h1>
          <p className="text-lg text-gray-600">
            تتبع تقدمك الأكاديمي وأداءك في الاختبارات
          </p>
        </div>

        {/* Overview Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          {/* Video Progress Card */}
          <div className="bg-white rounded-2xl shadow-lg p-6 border border-orange-100">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">تقدم المحاضرات</h3>
                <p className="text-sm text-gray-600">الفيديوهات المشاهدة</p>
              </div>
              <PlayCircle className="w-8 h-8 text-blue-500" />
            </div>
            <div className="flex items-center justify-center mb-4">
              <CircularProgress percentage={videoProgress} color="#3b82f6" />
            </div>
            <div className="text-center">
              <p className="text-sm text-gray-600">
                {lessonsData?.watchedVideos || 0} من {lessonsData?.totalVideos || 0} فيديو
              </p>
            </div>
          </div>

          {/* Quiz Progress Card */}
          <div className="bg-white rounded-2xl shadow-lg p-6 border border-orange-100">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">تقدم الاختبارات</h3>
                <p className="text-sm text-gray-600">الاختبارات المكتملة</p>
              </div>
              <BookOpen className="w-8 h-8 text-green-500" />
            </div>
            <div className="flex items-center justify-center mb-4">
              <CircularProgress percentage={quizProgress} color="#10b981" />
            </div>
            <div className="text-center">
              <p className="text-sm text-gray-600">
                {quizzesData?.solvedQuizzes || 0} من {quizzesData?.totalQuizzes || 0} اختبار
              </p>
            </div>
          </div>

          {/* Average Score Card */}
          <div className="bg-white rounded-2xl shadow-lg p-6 border border-orange-100">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">متوسط الدرجات</h3>
                <p className="text-sm text-gray-600">الأداء العام</p>
              </div>
              <Award className="w-8 h-8 text-orange-500" />
            </div>
            <div className="flex items-center justify-center mb-4">
              <CircularProgress percentage={averageScore} color="#f97316" />
            </div>
            <div className="text-center">
              <p className="text-sm text-gray-600">
                {averageScore.toFixed(1)}% متوسط عام
              </p>
            </div>
          </div>
        </div>

        {/* Detailed Statistics */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Quiz Results Chart */}
          <div className="bg-white rounded-2xl shadow-lg p-6 border border-orange-100">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-semibold text-gray-900">نتائج الاختبارات الأخيرة</h3>
              <BarChart3 className="w-6 h-6 text-orange-500" />
            </div>
            {quizzesData?.results && quizzesData.results.length > 0 ? (
              <BarChart data={quizzesData.results} />
            ) : (
              <div className="text-center py-8">
                <BookOpen className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">لا توجد نتائج اختبارات متاحة</p>
              </div>
            )}
          </div>

          {/* Progress Summary */}
          <div className="bg-white rounded-2xl shadow-lg p-6 border border-orange-100">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-semibold text-gray-900">ملخص التقدم</h3>
              <TrendingUp className="w-6 h-6 text-green-500" />
            </div>
            <div className="space-y-6">
              <div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg">
                <div className="flex items-center">
                  <PlayCircle className="w-5 h-5 text-blue-500 ml-3" />
                  <span className="text-sm font-medium text-gray-900">إجمالي الفيديوهات المشاهدة</span>
                </div>
                <span className="text-sm font-bold text-blue-600">{lessonsData?.watchedVideos || 0}</span>
              </div>
              
              <div className="flex items-center justify-between p-4 bg-green-50 rounded-lg">
                <div className="flex items-center">
                  <BookOpen className="w-5 h-5 text-green-500 ml-3" />
                  <span className="text-sm font-medium text-gray-900">إجمالي الاختبارات المكتملة</span>
                </div>
                <span className="text-sm font-bold text-green-600">{quizzesData?.solvedQuizzes || 0}</span>
              </div>
              
              <div className="flex items-center justify-between p-4 bg-orange-50 rounded-lg">
                <div className="flex items-center">
                  <Award className="w-5 h-5 text-orange-500 ml-3" />
                  <span className="text-sm font-medium text-gray-900">أعلى درجة</span>
                </div>
                <span className="text-sm font-bold text-orange-600">
                  {quizzesData?.results && quizzesData.results.length > 0 
                    ? Math.max(...quizzesData.results.map(r => r.score)).toFixed(1) + '%'
                    : '0%'}
                </span>
              </div>
              
              <div className="flex items-center justify-between p-4 bg-purple-50 rounded-lg">
                <div className="flex items-center">
                  <Calendar className="w-5 h-5 text-purple-500 ml-3" />
                  <span className="text-sm font-medium text-gray-900">آخر نشاط</span>
                </div>
                <span className="text-sm font-bold text-purple-600">
                  {quizzesData?.results && quizzesData.results.length > 0 
                    ? new Date(Math.max(...quizzesData.results.map(r => new Date(r.submittedAt).getTime()))).toLocaleDateString('ar-EG')
                    : 'لا يوجد'}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Achievement Badges */}
        {(videoProgress >= 50 || quizProgress >= 50 || averageScore >= 80) && (
          <div className="mt-12 bg-white rounded-2xl shadow-lg p-6 border border-orange-100">
            <h3 className="text-xl font-semibold text-gray-900 mb-6 text-center">الإنجازات المحققة</h3>
            <div className="flex flex-wrap justify-center gap-4">
              {videoProgress >= 50 && (
                <div className="flex flex-col items-center p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <PlayCircle className="w-8 h-8 text-blue-500 mb-2" />
                  <span className="text-sm font-medium text-blue-700">مشاهد نشط</span>
                  <span className="text-xs text-blue-600">50%+ من الفيديوهات</span>
                </div>
              )}
              
              {quizProgress >= 50 && (
                <div className="flex flex-col items-center p-4 bg-green-50 rounded-lg border border-green-200">
                  <BookOpen className="w-8 h-8 text-green-500 mb-2" />
                  <span className="text-sm font-medium text-green-700">متقدم في الاختبارات</span>
                  <span className="text-xs text-green-600">50%+ من الاختبارات</span>
                </div>
              )}
              
              {averageScore >= 80 && (
                <div className="flex flex-col items-center p-4 bg-orange-50 rounded-lg border border-orange-200">
                  <Award className="w-8 h-8 text-orange-500 mb-2" />
                  <span className="text-sm font-medium text-orange-700">أداء متميز</span>
                  <span className="text-xs text-orange-600">+80% متوسط الدرجات</span>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
    </>
  );
};

export default Statistics;
